<template>
  <div>
    <TotalInfoBlock v-if="paginationData?.total" :total="parseInt(paginationData?.total)" />
    <div v-if="isMobileDevice">
      <div v-if="items.length">
        <NoticeCard
          v-for="(noticeItem, index) of items"
          :id="noticeItem.id"
          :key="index"
          :is-own="true"
          :status="noticeItem?.status"
          :moderation-status="noticeItem.modStatus"
          :image="noticeItem.image"
          :name="noticeItem.name.primaryRow"
          :price="noticeItem.price"
          :price-general="noticeItem.priceGeneral"
          :quantity="noticeItem.quantity"
          :location="noticeItem.location"
          :description="noticeItem.description"
          class="notice-card mb-4"
        />
      </div>
      <SkeletonLoaderCard v-else :table-headers="resolvedTableHeaders" />
    </div>
    <div v-else class="notice-list">
      <OurTable
        :headers="resolvedTableHeaders"
        :items="tableItems"
        :filters="filters"
        :table-fixed="false"
        class="notice-list-table"
      >
        <template #cell-image="{ value: image }">
          <div class="image-wrapper">
            <img alt="img" :src="image" @error="errorImageHandler" />
          </div>
        </template>

        <template #cell-description="{ value: description }">
          <div class="text-ellipsis">
            {{ description.description }}
          </div>
          <div class="location">
            {{ description.location }}
          </div>
        </template>

        <template #cell-price="{ value: price }">
          <OurMoney
            weight="regular"
            size="sm"
            :sum="price.sum"
            :currency-symbol="price.currencySymbol"
            :positive-sign="false"
            :negative-sign="false"
          />
        </template>

        <template #cell-priceGeneral="{ value: priceGeneral }">
          <OurMoney
            weight="regular"
            size="sm"
            :sum="priceGeneral.sum"
            :currency-symbol="priceGeneral.currencySymbol"
            :positive-sign="false"
            :negative-sign="false"
          />
        </template>

        <template #cell-status="{ value: status }">
          <OurTag size="md" :text="status.text" :color="status.color" />
        </template>

        <template #cell-modStatus="{ value: modStatus }">
          <OurTag size="md" :text="modStatus.text" :color="modStatus.color" />
        </template>
      </OurTable>

      <OurPagination
        v-if="isMoreThanOnePage"
        :current-page="paginationData.currentPage"
        :per-page="paginationData.perPage"
        :total="paginationData.total"
        :disabled="isLoading"
        @pageChange="onPageChange"
      />
    </div>
  </div>
</template>

<script>
import NoticeCard from "@/views/flea-market/index/_components/NoticeCard.vue";
import SkeletonLoaderCard from "@/ui/loader/SkeletonLoaderCard.vue";
import { mapGetters } from "vuex";
import TotalInfoBlock from "@/components/TotalInfoBlock.vue";
import { buildNewNoticesListTableHeaders } from "@/views/settings/new-notices/index/config/newNoticesListTableHeaders";

export default {
  name: "NoticeList",

  components: { NoticeCard, SkeletonLoaderCard, TotalInfoBlock },

  props: {
    items: {
      type: Array,
      default: () => [],
    },

    filters: {
      type: Object,
      default: () => ({}),
    },

    isOwn: {
      type: Boolean,
      default: false,
    },

    paginationData: {
      type: Object,
      default: () => {},
    },

    isLoading: {
      type: Boolean,
      default: false,
    },

    headers: {
      type: Array,
      default: null,
    },
  },

  computed: {
    ...mapGetters("breakpoint", ["isMobileDevice"]),
    icons: () => ({
      Close: require("@/components/_bit/IconSvg/icons/Close.svg"),
    }),

    includeModerationStatus() {
      return this.items.some((item) => item.modStatus);
    },

    resolvedTableHeaders() {
      if (this.headers) {
        return this.headers;
      }

      return buildNewNoticesListTableHeaders({
        t: (key) => this.$t(key),
        isOwn: this.isOwn,
        includeModerationStatus: this.includeModerationStatus,
      });
    },

    tableItems() {
      return this.items.map((item) => {
        const {
          id,
          image,
          name,
          description,
          location,
          quantity,
          price,
          priceGeneral,
          status,
          modStatus,
        } = item;

        return {
          id,
          image,
          name,
          description: {
            description,
            location,
          },
          quantity,
          price,
          priceGeneral,
          status,
          modStatus,
          page: "NewNoticesDetails",
        };
      });
    },

    isMoreThanOnePage() {
      return this.paginationData.lastPage > 1;
    },

    routePage() {
      return Number.parseInt(this.$route.query.page, 10) || 1;
    },
  },

  watch: {
    routePage(newPage) {
      if (newPage !== this.paginationData.currentPage) {
        this.$emit("pageChange", newPage);
      }
    },
  },

  methods: {
    onPageChange(page) {
      this.$emit("pageChange", page);

      if (page !== this.routePage) {
        const query = { ...this.$route.query };

        delete query.page;

        const nextQuery = page === 1 ? query : { ...query, page };

        this.$router.push({ name: this.$route.name, query: nextQuery });
      }
    },

    errorImageHandler(event) {
      event.target.src = require("@/components/_bit/IconSvg/icons/Image.svg");
    },

  },
};
</script>

<style lang="postcss" scoped>
.notice-list {
  @apply h-full w-full;

  &-table:deep {
    thead {
      @apply whitespace-nowrap;
    }

    .image-wrapper {
      @apply p-1;
      @apply flex justify-center;
      @apply rounded border border-solid border-gray-100;
      @apply h-16 w-16;

      img {
        @apply object-contain;
      }
    }

    .name > div {
      @apply min-w-[10rem] max-w-[19.313rem];
      @apply whitespace-normal;
    }

    .description > div {
      @apply min-w-[10rem] max-w-[24.5rem];
      @apply whitespace-normal;
      @apply overflow-hidden;
    }

    .text-ellipsis {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .location {
      @apply mt-1;
      @apply text-xs text-gray-500;
    }

    .price {
      @apply w-16 text-right;

      .sum {
        @apply text-gray-900;

        .penny {
          @apply text-sm;
        }
      }
    }
  }
}
</style>
